// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { apiService } from "../../../services/api/apiService";
import { ApiServiceError } from "../../../services/api/apiError";
import { createRequestCache } from "../../../services/api/requestCache";
import { requireToken } from "../../../services/api/demoData";
import { isTestMode } from "../../../utils/runtimeEnv";
import type { WizardData } from "../models/onboardingModels";

export class WorkspaceSettingsSaveError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "WorkspaceSettingsSaveError";
  }
}

interface ApiEnvelope<T> {
  data?: T;
  error?: {
    message: string;
  };
}

function unwrapApiEnvelope<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.error !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

export type WorkspaceSettingsUpdateMode = "partial" | "full";

export interface WorkspaceSettingsUpdateRequest {
  mode: WorkspaceSettingsUpdateMode;
  settings: Partial<WizardData> | WizardData;
}

export interface WorkspaceSettingsUpdateOptions {
  token?: string;
  maxAttempts?: number;
  initialRetryDelayMs?: number;
  maxRetryDelayMs?: number;
  allowTestModeRequests?: boolean;
}

interface WorkspaceSettingsApiRequestBody {
  update: WorkspaceSettingsUpdateRequest;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INITIAL_RETRY_DELAY_MS = 250;
const DEFAULT_MAX_RETRY_DELAY_MS = 1500;

const WORKSPACE_SETTINGS_READ_TTL_MS = 5_000;
const workspaceSettingsReads = createRequestCache(WORKSPACE_SETTINGS_READ_TTL_MS);

export function __resetWorkspaceSettingsReadCacheForTests(): void {
  workspaceSettingsReads.invalidate();
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

function shouldRetry(error: unknown): boolean {
  if (error instanceof ApiServiceError) {
    return error.status === 429 || error.status >= 500;
  }

  return error instanceof TypeError;
}

function getRetryDelayMs(
  attempt: number,
  initialRetryDelayMs: number,
  maxRetryDelayMs: number,
): number {
  const exponentialDelay = initialRetryDelayMs * 2 ** Math.max(0, attempt - 1);
  return Math.min(maxRetryDelayMs, exponentialDelay);
}

function normalizeMaxAttempts(maxAttempts?: number): number {
  if (!Number.isFinite(maxAttempts)) {
    return DEFAULT_MAX_ATTEMPTS;
  }

  const wholeNumber = Math.trunc(maxAttempts as number);
  return Math.max(1, wholeNumber);
}

export async function updateWorkspaceSettings(
  update: WorkspaceSettingsUpdateRequest,
  options?: WorkspaceSettingsUpdateOptions,
): Promise<void> {
  const {
    token,
    maxAttempts,
    initialRetryDelayMs = DEFAULT_INITIAL_RETRY_DELAY_MS,
    maxRetryDelayMs = DEFAULT_MAX_RETRY_DELAY_MS,
    allowTestModeRequests = false,
  } = options ?? {};

  if (isTestMode() && !allowTestModeRequests) {
    return;
  }

  if (!token) {
    throw new WorkspaceSettingsSaveError(
      "A workspace-settings save needs an access token; nothing was saved.",
    );
  }

  const totalAttempts = normalizeMaxAttempts(maxAttempts);

  for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
    try {
      await apiService.requestJson<unknown, WorkspaceSettingsApiRequestBody>({
        path: "/api/onboarding/workspace-settings",
        method: "PATCH",
        token,
        body: { update },
      });

      workspaceSettingsReads.invalidate(token);

      return;
    } catch (error: unknown) {
      const isLastAttempt = attempt >= totalAttempts;

      if (isLastAttempt || !shouldRetry(error)) {
        throw error;
      }

      const retryDelayMs = getRetryDelayMs(
        attempt,
        initialRetryDelayMs,
        maxRetryDelayMs,
      );
      await wait(retryDelayMs);
    }
  }
}

async function readWorkspaceSettings(
  token: string,
): Promise<Partial<WizardData>> {
  const response = await apiService.requestJson<
    ApiEnvelope<{ settings: Partial<WizardData> }>
  >({
    path: "/api/onboarding/workspace-settings",
    method: "GET",
    token,
  });

  return response
    .map(unwrapApiEnvelope)
    .map((data) => data.settings)
    .unwrap();
}

export async function getWorkspaceSettings(
  token?: string,
  options?: Pick<WorkspaceSettingsUpdateOptions, "allowTestModeRequests">,
): Promise<Partial<WizardData>> {
  if (isTestMode() && !options?.allowTestModeRequests) {
    return {};
  }

  const requiredToken = requireToken({ token });
  return workspaceSettingsReads.run(requiredToken, () => readWorkspaceSettings(requiredToken));
}

export async function saveWorkspaceSettingsSnapshot(
  snapshot: WizardData,
  token?: string,
  options?: Omit<WorkspaceSettingsUpdateOptions, "token">,
): Promise<void> {
  const updateOptions: WorkspaceSettingsUpdateOptions = {
    ...(options ?? {}),
    ...(token ? { token } : {}),
  };

  await updateWorkspaceSettings(
    {
      mode: "full",
      settings: snapshot,
    },
    updateOptions,
  );
}

export async function patchWorkspaceSettings(
  patch: Partial<WizardData>,
  token?: string,
  options?: Omit<WorkspaceSettingsUpdateOptions, "token">,
): Promise<void> {
  const updateOptions: WorkspaceSettingsUpdateOptions = {
    ...(options ?? {}),
    ...(token ? { token } : {}),
  };

  await updateWorkspaceSettings(
    {
      mode: "partial",
      settings: patch,
    },
    updateOptions,
  );
}
